<template>
  <Dialog v-model:open="model">
    <DialogContent class="overflow-y-auto max-h-[810px] bg-dark1">
      <DialogHeader>
        <DialogTitle class="text-xl text-white font-semibold">
          Add credits
        </DialogTitle>

        <DialogClose
          class="rounded-sm opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-gray-950 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-gray-100 data-[state=open]:text-white dark:ring-offset-gray-950 dark:focus:ring-gray-300"
          @click="emit('update:open')"
        >
          <X class="w-6 h-6 text-dark4" />
          <span class="sr-only">Close</span>
        </DialogClose>
      </DialogHeader>
      <BaseDivider alignment="horizontal" />
      <section class="flex flex-col gap-4">
        <VendorSettingsCreditRadioGroup
          :options="creditOptions"
          @update:model-value="handleUpdate"
        >
          <template #content="{ option, isSelected }">
            <div class="flex justify-between items-center w-full">
              <span
                :class="[
                  isSelected ? 'text-secondary' : 'text-white',
                  'text-lg leading-7'
                ]"
                >{{ option.name }}</span
              >
              <svgo-check v-if="isSelected" class="text-secondary" />
            </div>
            <span :class="[isSelected ? 'text-white' : 'text-dark4']">{{
              option.price_current_formatted
            }}</span>
          </template>
        </VendorSettingsCreditRadioGroup>
        <p class="text-dark4 text-base leading-6">
          Purchased credits are permanent and do not reset.
        </p>
      </section>
      <DialogFooter>
        <div class="flex justify-between items-center gap-4 w-full">
          <DialogClose
            class="text-white bg-dark4 bg-opacity-5 hover:bg-opacity-15 hover:duration-300 flex-grow px-3 py-2"
            @click="emit('update:open')"
          >
            Cancel
          </DialogClose>
          <BaseButton
            @click="handleCreditsPurchase"
            :disabled="isPurchasing"
            class="bg-primary hover:bg-secondary flex gap-2 justify-center items-center hover:duration-300 text-dark1 flex-grow px-3 py-2"
          >
            <LoadingSpinner v-if="isPurchasing" size="small" />
            <span>Purchase</span>
          </BaseButton>
        </div>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
<script setup>
  import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle
  } from '@/components/ui-library/dialog';
  import { getCreditPackages, processPurchase } from '@/services';
  import { useCreditsStore } from '@/stores/credits/credits';
  import { X } from 'lucide-vue-next';
  const model = defineModel();
  const emit = defineEmits(['update:open']);
  const { $toast } = useNuxtApp();
  const creditOptions = ref([]);
  const selectedPackage = ref(1);
  const isPurchasing = ref(false);

  onMounted(() => {
    getCreditPackages()
      .then((response) => {
        creditOptions.value = response.data.value?.data;
      })
      .catch((error) => {
        console.error('Error fetching credit packages:', error);
      });
  });

  const handleUpdate = (value) => {
    selectedPackage.value = value;
  };

  const handleCreditsPurchase = async () => {
    if (!selectedPackage.value) return;

    try {
      const store = useCreditsStore();
      isPurchasing.value = true;

      const payload = {
        items: [{ purchasable_id: selectedPackage.value, quantity: 1 }],
        entity: 'vendor'
      };

      const response = await processPurchase(payload, 'recurly');
      if (response.data.value.status) {
        await store.recalculateCredits();
        $toast.success('Credits purchased successfully!');
        emit('update:open');
      } else {
        $toast.error(response.data.value.error || 'Failed to process payment');
      }
    } catch (error) {
      console.error('Error processing purchase:', error);
      $toast.error('Failed to process payment.');
    } finally {
      isPurchasing.value = false;
      selectedPackage.value = 1;
    }
  };
</script>
